// Reusable SVG icons rendered with a soft pastel gradient fill.
// Each icon accepts size + className props. The gradient is defined inline
// (with a per-icon unique id so multiple instances coexist) and the stop
// COLORS come from CSS variables (--c-icon-1/2/3), which makes the icons
// automatically re-theme when <html data-theme="…"> changes.
//
// Day:   pink (#FF66AC) → yellow (#FFE049) → mint (#3FE2A5)
// Night: pink (#FF66AC) → purple (#A47BFF) → blue (#58AEFF)
//
// IMPORTANT: SVG <stop>'s `stop-color` attribute is a CSS property, so
// `var(--c-icon-1)` works only via the `style` prop, NOT via the
// `stopColor` JSX attribute. We use style to be safe.
import React from 'react';

// Six-stop "SWAG" cyclic gradient — used by MicIcon + CloseIcon (mid-app
// icons). Cycle: c1 → c2 → c3 → c3 → c2 → c3. The doubled mid-end stop at
// 60% replaces the pink loop-back, which kills a stray pink smudge that
// used to land in the bottom-left of the icon on the previous version.
function SwagStops() {
  return (
    <>
      <stop offset="0%"   style={{ stopColor: 'var(--c-icon-1)' }} />
      <stop offset="20%"  style={{ stopColor: 'var(--c-icon-2)' }} />
      <stop offset="40%"  style={{ stopColor: 'var(--c-icon-3)' }} />
      <stop offset="60%"  style={{ stopColor: 'var(--c-icon-3)' }} />
      <stop offset="80%"  style={{ stopColor: 'var(--c-icon-2)' }} />
      <stop offset="100%" style={{ stopColor: 'var(--c-icon-3)' }} />
    </>
  );
}

// "B3" gradient — vivid 6-stop swag with a PINK LOOP-BACK at 60%. Visually
// it's the SwagStops pattern but the doubled-mid stop is c1 instead of c3,
// which yields a richer second pink "wave" diagonally across the icon.
//
// Per the user, this is the audio-post button's chosen palette in BOTH
// the day and the night theme — the colors come from CSS variables so
// it auto-swaps:
//    Day:   pink → yellow → mint → pink → yellow → mint
//    Night: pink → purple → blue → pink → purple → blue
function B3PinkLoopStops() {
  return (
    <>
      <stop offset="0%"   style={{ stopColor: 'var(--c-icon-1)' }} />
      <stop offset="20%"  style={{ stopColor: 'var(--c-icon-2)' }} />
      <stop offset="40%"  style={{ stopColor: 'var(--c-icon-3)' }} />
      <stop offset="60%"  style={{ stopColor: 'var(--c-icon-1)' }} />
      <stop offset="80%"  style={{ stopColor: 'var(--c-icon-2)' }} />
      <stop offset="100%" style={{ stopColor: 'var(--c-icon-3)' }} />
    </>
  );
}

// Three-stop simple top-down gradient — used by paperclip.
// Equal thirds: c1 (top) → c2 (mid) → c3 (bottom).
function VerticalThirdsStops() {
  return (
    <>
      <stop offset="0%"   style={{ stopColor: 'var(--c-icon-1)' }} />
      <stop offset="50%"  style={{ stopColor: 'var(--c-icon-2)' }} />
      <stop offset="100%" style={{ stopColor: 'var(--c-icon-3)' }} />
    </>
  );
}

// V4 "audio" gradient — pink → peach → yellow → mint, four hardcoded stops,
// horizontal direction. Used by both <Waveform/> (audio-bubble bars) AND
// the play / pause buttons inside the audio bubble, so the entire audio
// surface reads as one coherent gradient. Locked to V4 (NOT theme-aware)
// because the user explicitly chose this palette for audio surfaces.
function V4AudioStops() {
  return (
    <>
      <stop offset="0%"   stopColor="#FF85C0" />
      <stop offset="35%"  stopColor="#FFC4A8" />
      <stop offset="70%"  stopColor="#FFE49B" />
      <stop offset="100%" stopColor="#88E0B5" />
    </>
  );
}

let _uid = 0;
function useUid(prefix) {
  return React.useMemo(
    () => `${prefix}-${++_uid}-${Math.random().toString(36).slice(2, 7)}`,
    [prefix],
  );
}

export function PaperclipIcon({ size = 20, className = '' }) {
  const id = useUid('grad-clip');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
          <VerticalThirdsStops />
        </linearGradient>
      </defs>
      <path
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.44 11.05 12.25 20.24a5.5 5.5 0 1 1-7.78-7.78l9.19-9.19a3.5 3.5 0 1 1 4.95 4.95L9.41 17.42a1.5 1.5 0 1 1-2.12-2.12l8.49-8.49"
      />
    </svg>
  );
}

export function MicIcon({ size = 20, className = '' }) {
  const id = useUid('grad-mic');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={id} gradientTransform="rotate(135)">
          <SwagStops />
        </linearGradient>
      </defs>
      <path fill={`url(#${id})`} d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z" />
      <path
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth="2"
        strokeLinecap="round"
        d="M19 11a7 7 0 0 1-14 0M12 18v3M8 21h8"
      />
    </svg>
  );
}

export function AudioWaveIcon({ size = 20, className = '' }) {
  const id = useUid('grad-wave');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        {/* B3 — vivid 6-stop swag with a pink loop-back. Theme-aware: the
            colors come from CSS variables, so the bars are pink/yellow/mint
            during the day window and pink/purple/blue at night.
            Chosen by the user for the audio-post (record) button. */}
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <B3PinkLoopStops />
        </linearGradient>
      </defs>
      <g fill={`url(#${id})`}>
        <rect x="2" y="10" width="2.6" height="4" rx="1.3" />
        <rect x="6.5" y="7" width="2.6" height="10" rx="1.3" />
        <rect x="11" y="3" width="2.6" height="18" rx="1.3" />
        <rect x="15.5" y="6" width="2.6" height="12" rx="1.3" />
        <rect x="20" y="9" width="2.6" height="6" rx="1.3" />
      </g>
    </svg>
  );
}

export function PlayIcon({ size = 18, className = '' }) {
  const id = useUid('grad-play');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        {/* V4 audio gradient — matches the audio-bubble waveform so the
            play button and the bars read as one coherent surface.
            Horizontal direction (rotate(0)) to match Waveform. */}
        <linearGradient id={id} gradientTransform="rotate(0)">
          <V4AudioStops />
        </linearGradient>
      </defs>
      <path fill={`url(#${id})`} d="M7 5v14l12-7z" />
    </svg>
  );
}

export function PauseIcon({ size = 18, className = '' }) {
  const id = useUid('grad-pause');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        {/* V4 audio gradient — same as PlayIcon for visual continuity
            while the user is mid-playback. */}
        <linearGradient id={id} gradientTransform="rotate(0)">
          <V4AudioStops />
        </linearGradient>
      </defs>
      <rect x="6" y="5" width="4" height="14" rx="1" fill={`url(#${id})`} />
      <rect x="14" y="5" width="4" height="14" rx="1" fill={`url(#${id})`} />
    </svg>
  );
}

export function CloseIcon({ size = 12, className = '' }) {
  const id = useUid('grad-close');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={id} gradientTransform="rotate(135)">
          <SwagStops />
        </linearGradient>
      </defs>
      <path
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth="3"
        strokeLinecap="round"
        d="M6 6l12 12M18 6L6 18"
      />
    </svg>
  );
}
